import React, { useEffect, useRef, useState } from "react";
import { ElementProps } from "elements/base/provider";
import { Pdf } from "shared/datamodel/schemas/pdf";
import { IPdfController } from "./controller";
import useObservableController from "elements/hooks/use-observable-controller";
import { Rect, Image } from "react-konva";
import { Html } from "react-konva-utils";
import * as pdfjsLib from "pdfjs-dist";
import { FileMetadata, FileMetadataType } from "shared/datamodel/schemas/metadata";
import { ReadTransaction } from "@workcanvas/reflect";
import { useSubscribe } from "replicache-react";
import { canvasMetadataPrefix } from "shared/util/utils";
import { getPathPrefix } from "utils/getPathPrefix";

import { pdfCacheService } from "frontend/services/pdf-cache-service";
import { pdfPageCacheService } from "frontend/services/pdf-page-cache-service";
import fileElementStyle from "frontend/canvas-designer-new/elements/file-element/file-element.module.css";

// Set up PDF.js worker from public directory
pdfjsLib.GlobalWorkerOptions.workerSrc = getPathPrefix("/pdf/pdf.worker.min.js");

function getSpinnerStyles(width: number, height: number) {
  const spinnerSize = Math.min(width, height) * 0.1;
  const spinnerContainerStyle = {
    width: `${spinnerSize}px`,
    height: `${spinnerSize}px`,
    padding: `${spinnerSize * 0.1}px`,
    borderRadius: `${spinnerSize * 0.15}px`,
  };
  const spinnerStyle = {
    width: `${spinnerSize * 0.6}px`,
    height: `${spinnerSize * 0.6}px`,
    borderWidth: `${spinnerSize * 0.1}px`,
    borderRadius: "50%",
  };
  return { spinnerContainerStyle, spinnerStyle };
}

export default function PdfElementComponent({ id, controller: _controller }: ElementProps<Pdf>) {
  const controller = _controller as IPdfController;
  useObservableController(controller);

  const element = controller.element;
  const [pdfStatus, setPdfStatus] = useState<"loading" | "loaded" | "error">("loading");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [renderedImage, setRenderedImage] = useState<HTMLImageElement | null>(null);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const currentPage = controller.getDisplayPage();
  const pageCache = useRef<Array<HTMLImageElement | null>>([]);
  const totalPages = controller.getTotalPages();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTaskRef = useRef<any | null>(null);
  const isRenderingRef = useRef<boolean>(false);
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRenderedPageRef = useRef<number>(currentPage);
  const RENDER_DEBOUNCE_MS = 150; // Debounce render calls during resizing

  // Subscribe to file metadata
  const metadata = useSubscribe(
    controller.context.reflect,
    async (tx: ReadTransaction) => {
      if (!element.fileId) return null;
      const metadata = await tx.get(`${canvasMetadataPrefix}${FileMetadataType}-${element.fileId.toString()}`);
      return metadata as FileMetadata | null;
    },
    null,
    [element.fileId]
  );

  // Load PDF document using cache service
  useEffect(() => {
    if (!metadata?.url || !element.fileId) return;

    let isMounted = true;

    const loadPdf = async () => {
      try {
        setPdfStatus("loading");

        const pdfDocument = await pdfCacheService.getPDF(element.fileId?.toString() || "", metadata.url);

        if (isMounted) {
          // Update total pages if different
          if (pdfDocument.numPages !== totalPages) {
            controller.patchElement(id, (draft) => {
              (draft as Pdf).totalPages = pdfDocument.numPages;
            });
          }
          setPdf(pdfDocument);
          setPdfStatus("loaded");
        }
      } catch (error) {
        console.error("Error loading PDF:", error);
        if (isMounted) {
          setPdfStatus("error");
        }
      }
    };

    loadPdf();

    // Cleanup: release reference
    return () => {
      isMounted = false;
      if (element.fileId) {
        pdfCacheService.release(element.fileId.toString());
      }
    };
  }, [metadata?.url, element.fileId, controller, totalPages, id]);

  // Render current page
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch {
            // Ignore cancellation errors
          }
          renderTaskRef.current = null;
        }

        // Wait for any ongoing rendering to complete
        if (isRenderingRef.current) {
          return;
        }

        isRenderingRef.current = true;

        const pageToRender = controller.getDisplayPage();
        const isPageChange = lastRenderedPageRef.current !== pageToRender;

        if (isPageChange) {
          setIsPageLoading(true);
        }

        // Check shared page cache first
        if (element.fileId) {
          const cachedPageImage = pdfPageCacheService.getPage(element.fileId.toString(), pageToRender);
          if (cachedPageImage) {
            setRenderedImage(cachedPageImage);
            lastRenderedPageRef.current = pageToRender;
            setIsPageLoading(false);
            isRenderingRef.current = false;
            return;
          }
        }

        // Check local cache
        if (pageCache.current[pageToRender]) {
          setRenderedImage(pageCache.current[pageToRender]);
          lastRenderedPageRef.current = pageToRender;
          setIsPageLoading(false);
          isRenderingRef.current = false;
          return;
        }

        const page = await pdf.getPage(pageToRender);

        const baseViewport = page.getViewport({ scale: 1 });

        // Calculate scale to fit within element dimensions
        const scaleX = element.width / baseViewport.width;
        const scaleY = element.height / baseViewport.height;
        const fitScale = Math.min(scaleX, scaleY);

        // Apply a quality multiplier for sharper rendering (2x for high quality)
        const qualityMultiplier = 3;
        const renderScale = fitScale * qualityMultiplier;

        const viewport = page.getViewport({ scale: renderScale });

        const canvas = canvasRef.current;
        if (!canvas) {
          isRenderingRef.current = false;
          return;
        }

        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) {
          isRenderingRef.current = false;
          return;
        }

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        // Store the render task so we can cancel it if needed
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;

        // Clear the render task reference
        renderTaskRef.current = null;

        canvas.toBlob((blob) => {
          if (blob) {
            const img = document.createElement("img");
            img.addEventListener("load", () => {
              setRenderedImage(img);
              pageCache.current[pageToRender] = img;

              if (element.fileId) {
                pdfPageCacheService.setPage(element.fileId.toString(), pageToRender, img);
              }
              lastRenderedPageRef.current = pageToRender;
              setIsPageLoading(false);
              isRenderingRef.current = false;
            });
            img.src = URL.createObjectURL(blob);
          } else {
            setIsPageLoading(false);
            isRenderingRef.current = false;
          }
        });
      } catch (error) {
        // Check if error is due to cancellation
        const errorName = (error as Error)?.name;
        if (error && errorName !== "RenderingCancelledException") {
          console.error("Error rendering page:", error);
        }
        setIsPageLoading(false);
        isRenderingRef.current = false;
        renderTaskRef.current = null;
      }
    };

    // Clear any existing timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // Debounce the render call
    renderTimeoutRef.current = setTimeout(() => {
      renderPage();
    }, RENDER_DEBOUNCE_MS);

    // Cleanup function to cancel any ongoing render when component unmounts or dependencies change
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
      }
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null;
      }
    };
  }, [pdf, currentPage, element.width, element.height, element.fileId, controller]);

  return (
    <>
      <Rect width={element.width} height={element.height} fill="#ffffff" stroke="#e0e0e0" strokeWidth={1} />

      {renderedImage && <Image image={renderedImage} width={element.width} height={element.height} x={0} y={0} />}

      <Html>
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Html>

      {pdfStatus === "loading" && (
        <Html>
          <div
            style={{
              width: `${element.width}px`,
              height: `${element.height}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <div style={{ textAlign: "center", color: "#666" }}>
              <div>Loading PDF...</div>
            </div>
          </div>
        </Html>
      )}

      {pdfStatus === "error" && (
        <Html>
          <div
            style={{
              width: `${element.width}px`,
              height: `${element.height}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <div style={{ textAlign: "center", color: "#666" }}>
              <div>Failed to load PDF</div>
            </div>
          </div>
        </Html>
      )}

      {pdfStatus === "loaded" && isPageLoading && (
        <Html>
          <div
            style={{
              width: `${element.width}px`,
              height: `${element.height}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 10,
            }}
          >
            <div
              className={fileElementStyle.loadingSpinnerContainer}
              style={getSpinnerStyles(element.width, element.height).spinnerContainerStyle}
            >
              <div
                className={fileElementStyle.loadingSpinner}
                style={getSpinnerStyles(element.width, element.height).spinnerStyle}
              />
            </div>
          </div>
        </Html>
      )}
    </>
  );
}
